import { Injectable } from '@angular/core';

export type CloudflareTurnstileApi = {
  render: (container: string | HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

export function getTurnstileApi(): CloudflareTurnstileApi | null {
  const t = (globalThis as unknown as { turnstile?: CloudflareTurnstileApi }).turnstile;
  return t ?? null;
}

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const SCRIPT_DATA_ATTR = 'data-cloudflare-turnstile';

/**
 * Lazy-loads Cloudflare Turnstile `api.js` once; safe to call from multiple components.
 * Used by onboarding so the script is not loaded on every app route.
 */
@Injectable({ providedIn: 'root' })
export class TurnstileScriptService {
  private loadPromise: Promise<void> | null = null;

  load(): Promise<void> {
    if (this.isApiReady()) {
      return Promise.resolve();
    }
    if (this.loadPromise) {
      return this.loadPromise;
    }
    this.loadPromise = this.appendScriptOnce();
    return this.loadPromise;
  }

  private isApiReady(): boolean {
    return getTurnstileApi() !== null;
  }

  private appendScriptOnce(): Promise<void> {
    return new Promise((resolve, reject) => {
      const onApiReady = (): void => {
        if (this.isApiReady()) {
          resolve();
        } else {
          this.loadPromise = null;
          reject(new Error('Turnstile API not available after script load'));
        }
      };

      const existing = document.querySelector<HTMLScriptElement>(`script[${SCRIPT_DATA_ATTR}]`);
      if (existing) {
        if (this.isApiReady()) {
          resolve();
          return;
        }
        existing.addEventListener('load', onApiReady);
        existing.addEventListener('error', () => {
          this.loadPromise = null;
          reject(new Error('Turnstile script failed to load'));
        });
        return;
      }

      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.setAttribute(SCRIPT_DATA_ATTR, 'true');
      script.onload = onApiReady;
      script.onerror = () => {
        this.loadPromise = null;
        reject(new Error('Turnstile script failed to load'));
      };
      document.head.appendChild(script);
    });
  }
}
